import { AsyncPipe } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core'
import { MatIconButton } from '@angular/material/button'
import { MatDialog, MatDialogRef } from '@angular/material/dialog'
import { MatIcon } from '@angular/material/icon'
import { MatSort, MatSortHeader } from '@angular/material/sort'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
} from '@angular/material/table'
import { MatTooltip } from '@angular/material/tooltip'
import {
  CollectionUtil,
  ElderDataToolbarComponent,
  ElderStopEventPropagationDirective,
  ElderTableComponent,
  ElderTableToolbarDirective,
} from '@elderbyte/ngx-starter'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject } from 'rxjs'
import {
  IInstructionBrowserDialogOptions,
  InstructionBrowserDialogComponent,
} from '../../instructions/instruction-browser-dialog/instruction-browser-dialog.component'
import { OperationSegmentSpec } from '../../process-plan'
import {
  IOperationMaterialMappingDialogOptions,
  OperationMaterialMappingDetailDialogComponent,
} from '../material-mappings/operation-material-mapping-detail-dialog/operation-material-mapping-detail-dialog.component'
import {
  IOperationSegmentDialogOptions,
  OperationSegmentSpecDetailDialogComponent,
} from '../operation-segment-spec-detail-dialog/operation-segment-spec-detail-dialog.component'

@Component({
  selector: 'app-operation-segment-browser',
  templateUrl: './operation-segment-browser.component.html',
  styleUrls: ['./operation-segment-browser.component.scss'],
  imports: [
    ElderTableComponent,
    MatSort,
    ElderTableToolbarDirective,
    ElderDataToolbarComponent,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatIcon,
    MatSortHeader,
    MatIconButton,
    ElderStopEventPropagationDirective,
    MatTooltip,
    AsyncPipe,
    ElderTranslatePipe,
  ],
})
export class OperationSegmentBrowserComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  public readonly operationSegmentSpecs$ = new BehaviorSubject<OperationSegmentSpec[]>([])

  public readonly materialMappingsEnabled$ = new BehaviorSubject<boolean>(false)
  private matDialog = inject(MatDialog)

  @Output()
  public readonly operationSegmentSpecsChange = new EventEmitter<OperationSegmentSpec[]>()

  @Output()
  public readonly onSave = new EventEmitter<Event>()

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  public get operationSegmentSpecs(): OperationSegmentSpec[] {
    return this.operationSegmentSpecs$.getValue()
  }

  @Input()
  public set operationSegmentSpecs(oss: OperationSegmentSpec[]) {
    this.operationSegmentSpecs$.next(oss)
  }

  @Input()
  public set materialMappingsEnabled(enabled: boolean) {
    this.materialMappingsEnabled$.next(enabled)
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public onOperationClicked(operation: OperationSegmentSpec): void {
    const dialogRef = this.openOperationDialog(operation)

    dialogRef
      .afterClosed()
      .subscribe((updatedOperation: OperationSegmentSpec) => this.updateAndSave(updatedOperation))
  }

  public addOperation(): void {
    const dialogRef = this.openOperationDialog(new OperationSegmentSpec())

    dialogRef.afterClosed().subscribe((oss: OperationSegmentSpec) => {
      if (oss && oss.action) {
        let specs: OperationSegmentSpec[] = []
        if (this.operationSegmentSpecs && this.operationSegmentSpecs.length > 0) {
          specs = [...this.operationSegmentSpecs]
        }
        specs.push(oss)
        this.operationSegmentSpecs = specs
        this.operationSegmentSpecsChange.emit(this.operationSegmentSpecs)
      }
    })
  }

  public onRemoveOperations(toRemove: OperationSegmentSpec[]) {
    toRemove.forEach((r) =>
      this.operationSegmentSpecs.splice(this.operationSegmentSpecs.indexOf(r), 1)
    )
    this.operationSegmentSpecs = [...this.operationSegmentSpecs]
  }

  public moveUp(op: OperationSegmentSpec): void {
    const specs = [...this.operationSegmentSpecs]
    CollectionUtil.moveUp(specs, op)
    this.operationSegmentSpecs = specs
    this.operationSegmentSpecsChange.emit(specs)
  }

  public moveDown(op: OperationSegmentSpec): void {
    const specs = [...this.operationSegmentSpecs]
    CollectionUtil.moveDown(specs, op)
    this.operationSegmentSpecs = specs
    this.operationSegmentSpecsChange.emit(specs)
  }

  public openInstructions(op: OperationSegmentSpec): void {
    const dialogRef = this.openInstructionDialog(op)

    dialogRef
      .afterClosed()
      .subscribe((updatedOperation: OperationSegmentSpec) => this.updateAndSave(updatedOperation))
  }

  public openMappings(op: OperationSegmentSpec): void {
    const dialogRef = this.openMappingDialog(op)

    dialogRef
      .afterClosed()
      .subscribe((updatedOperation: OperationSegmentSpec) => this.updateAndSave(updatedOperation))
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private updateAndSave(spec: OperationSegmentSpec): void {
    if (spec) {
      this.operationSegmentSpecs = [...this.operationSegmentSpecs] // Update table
      this.onSave.emit()
    }
  }

  private openOperationDialog(
    op: OperationSegmentSpec
  ): MatDialogRef<OperationSegmentSpecDetailDialogComponent> {
    return this.matDialog.open(OperationSegmentSpecDetailDialogComponent, {
      minWidth: '30vw',
      height: '30vh',
      data: <IOperationSegmentDialogOptions>{
        operation: op,
      },
    })
  }

  private openInstructionDialog(
    op: OperationSegmentSpec
  ): MatDialogRef<InstructionBrowserDialogComponent> {
    return this.matDialog.open(InstructionBrowserDialogComponent, {
      minWidth: '90vw',
      height: '90vh',
      data: <IInstructionBrowserDialogOptions>{
        operation: op,
      },
    })
  }

  private openMappingDialog(
    op: OperationSegmentSpec
  ): MatDialogRef<OperationMaterialMappingDetailDialogComponent> {
    return this.matDialog.open(OperationMaterialMappingDetailDialogComponent, {
      minWidth: '90vw',
      height: '90vh',
      data: <IOperationMaterialMappingDialogOptions>{
        operation: op,
      },
    })
  }
}
